/**
 * House rules: the short card set a cook who already holds a Utah food handler card sees instead of the 9 generic
 * lessons and 7 sign-offs. A card holder has been taught the general Food Code (hands, temperatures, storage);
 * what differs place to place is how THIS kitchen works, so that is all we ask them to read.
 *
 * Truth rules (G2b): the app never claims training the person doesn't have. A card on file is the person's word
 * plus a photo until a manager checks it; the words say "card on file", never "trained" or "certified".
 *
 * The law, from docs/source/research/business-setup-utah.md A8, which quotes Utah Code 26B-7-413:
 *   (2) a food handler completes an approved course "within 14 days after the day on which the person begins
 *       employment" and obtains a food handler permit "within 30 days";
 *   (5) a food handler shall obtain the permit "(a) before handling any food; (b) within 30 days of initial
 *       employment; and (c) within seven days of the expiration of an existing food handler permit".
 *   (6) valid statewide, for three years.
 * (2) and (5)(a) read together are ambiguous (the same source says so); the app shows the 30-day deadline that both
 * (2) and (5)(b) state and tells people to check with their county. The lessons themselves are the company's policy,
 * not Utah law, so a card holder skipping them is not skipping anything the statute asks for.
 */
import type { Acknowledgement } from "./acks.ts";
import { HOUSERULES_UT } from "./states/ut/houserules.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof HOUSERULES_UT>> = perStateTable("HOUSERULES", HOUSERULES_UT);
const stateWords = (state?: UsState) => wordsFor(WORDS, state);

/** The food handler card law for a state (absent = Utah). A field is null where the state has no verified rule. */
export function cardLawFor(state?: UsState) {
  const w = stateWords(state);
  return {
    citation: w.cardCitation,
    /** Shown to the person, truthfully: the deadline for the CARD, not for our lessons. */
    line: w.cardLine,
    days: w.cardDays,
  };
}

export const CARD_LAW = cardLawFor("UT");

export interface HouseCard {
  id: string;
  /** Icon name both apps draw (a web Icon / mobile Icon name). */
  icon: "thermo" | "sick" | "report" | "check";
  /** One idea, at most 12 words, verb first. */
  say: string;
  /** Why, in plain words, at most 14 words. */
  why: string;
  citation?: string;
}

/** Four cards: what is different about working here. Each line is short enough to read at a glance. */
export const HOUSE_CARDS: readonly HouseCard[] = [
  { id: "checkin", icon: "check", say: "Do the 30-second check-in before every shift.", why: "It is how we know you are well enough to work.", citation: "Food Code 2-201.11" },
  { id: "sick", icon: "sick", say: "Feeling sick? Tell your manager. Do not work with food.", why: "You will never be in trouble for telling us.", citation: "Food Code 2-201.11, 2-201.12" },
  { id: "log", icon: "thermo", say: "Log temperatures in the app when it asks.", why: "Your log is our proof for the inspector.", citation: "Food Code 3-501.16" },
  { id: "report", icon: "report", say: "See a problem? Tap Report and add a photo.", why: "A warm fridge or a pest is fixed faster when we know.", citation: "Food Code 6-501.111" },
] as const;

export const HOUSE_ACK_ID = "house";

/** The one signature for the whole set. Not part of ACKNOWLEDGEMENTS (the 7 generic forms), but getAck() finds it. */
export const HOUSE_ACK: Acknowledgement = {
  id: HOUSE_ACK_ID, version: 1, title: "House rules", citations: HOUSE_CARDS.map((c) => c.citation).filter(Boolean).join("; "),
  statements: HOUSE_CARDS.map((c) => c.say),
};

const words = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;
/** Words a card holder reads before the first log: the card lines and their reasons. */
export const houseRulesWords = () => HOUSE_CARDS.reduce((n, c) => n + words(c.say) + words(c.why), 0);
